import { Component, Message, UserPromptSuggestionsMessageSchema, CollectionRegistry } from '../types';
import { logger } from '../utils/logger';

/**
 * Handle user prompt suggestions request
 * Uses token-based search for components and PostgreSQL full-text search for bookmarks
 */
export async function handleUserPromptSuggestions(
  data: any,
  components: Component[],
  sendMessage: (message: Message) => void,
  collections: CollectionRegistry
): Promise<void> {
  try {
    const request = UserPromptSuggestionsMessageSchema.parse(data);
    const { id, payload, from } = request;

    const { prompt, userId, limit = 10 } = payload;
    const wsId = from.id;

    logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Processing user prompt suggestions: ${prompt}`);

    // Validate input
    if (!prompt || prompt.trim().length === 0) {
      sendResponse(id, {
        success: false,
        error: 'Prompt is required and cannot be empty'
      }, sendMessage, wsId);
      return;
    }

    // COMMENTED OUT: Semantic/embedding-based search (kept for potential future use)
    // const componentSearchHandler = collections?.['components']?.['search'];
    // const bookmarkSearchHandler = collections?.['bookmarks']?.['search'];
    //
    // let componentSuggestions: any[] = [];
    // let bookmarkSuggestions: any[] = [];
    // let useEmbeddingSearch = false;
    //
    // // Build parallel search promises
    // const searchPromises: Promise<void>[] = [];
    //
    // // Search components via embedding
    // if (componentSearchHandler) {
    //   searchPromises.push(
    //     (async () => {
    //       try {
    //         logger.info('Using embedding-based search for components');
    //         const result = await componentSearchHandler({ prompt, limit });
    //
    //         if (result.success && result.suggestions) {
    //           componentSuggestions = result.suggestions.map((s: any) => ({
    //             ...s,
    //             suggestionType: 'component'
    //           }));
    //           useEmbeddingSearch = true;
    //           logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Found ${componentSuggestions.length} component suggestions`);
    //         }
    //       } catch (embeddingError) {
    //         logger.warn('Component embedding search failed:', embeddingError);
    //       }
    //     })()
    //   );
    // }
    //
    // // Search bookmarks via embedding (if userId is available)
    // if (bookmarkSearchHandler && userId && userId !== 'anonymous') {
    //   searchPromises.push(
    //     (async () => {
    //       try {
    //         logger.info(`Using embedding-based search for bookmarks (user: ${userId})`);
    //         const result = await bookmarkSearchHandler({ prompt, userId, limit });
    //
    //         if (result.success && result.suggestions) {
    //           bookmarkSuggestions = result.suggestions.map((s: any) => ({
    //             ...s,
    //             suggestionType: 'bookmark'
    //           }));
    //           useEmbeddingSearch = true;
    //           logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Found ${bookmarkSuggestions.length} bookmark suggestions`);
    //         }
    //       } catch (embeddingError) {
    //         logger.warn('Bookmark embedding search failed:', embeddingError);
    //       }
    //     })()
    //   );
    // }
    //
    // // Wait for all searches to complete in parallel
    // if (searchPromises.length > 0) {
    //   await Promise.all(searchPromises);
    // }
    //
    // // If we got embedding results, combine and return
    // if (useEmbeddingSearch && (componentSuggestions.length > 0 || bookmarkSuggestions.length > 0)) {
    //   // Combine and sort by similarity (no threshold filtering - show all suggestions)
    //   const allSuggestions = [...componentSuggestions, ...bookmarkSuggestions]
    //     .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
    //     .slice(0, limit);
    //
    //   logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Returning all suggestions: ${componentSuggestions.length} components, ${bookmarkSuggestions.length} bookmarks`);
    //
    //   sendResponse(id, {
    //     success: true,
    //     data: {
    //       prompt,
    //       suggestions: allSuggestions,
    //       count: allSuggestions.length,
    //       componentCount: componentSuggestions.length,
    //       bookmarkCount: bookmarkSuggestions.length,
    //       message: `Found ${allSuggestions.length} suggestions (${componentSuggestions.length} components, ${bookmarkSuggestions.length} bookmarks)`
    //     }
    //   }, sendMessage, wsId);
    //   return;
    // }
    // END COMMENTED OUT SECTION

    // Use token-based search for components and PostgreSQL text search for bookmarks
    const displayComponents = components.filter(c => c.isDisplayComp === true);
    const bookmarkTextSearchHandler = collections?.['bookmarks']?.['textSearch'];

    logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Using token-based search for components (${displayComponents.length} display components) and PostgreSQL text search for bookmarks`);

    // Build parallel search promises
    const searchPromises: Promise<void>[] = [];
    let componentSuggestions: any[] = [];
    let bookmarkSuggestions: any[] = [];

    // Token-based search for components
    if (displayComponents.length > 0) {
      const componentResults = searchComponents(prompt, displayComponents, limit);
      componentSuggestions = componentResults.map((c: Component) => ({
        ...c,
        suggestionType: 'component',
        similarity: 1.0 // Token-based search doesn't have similarity scores
      }));
      logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Found ${componentSuggestions.length} component suggestions via token search`);
    }

    // PostgreSQL text search for bookmarks (if userId is available)
    if (bookmarkTextSearchHandler && userId && userId !== 'anonymous') {
      searchPromises.push(
        (async () => {
          try {
            logger.info(`Using PostgreSQL text search for bookmarks (user: ${userId})`);
            const result = await bookmarkTextSearchHandler({ prompt, userId, limit });

            if (result.success && result.suggestions) {
              bookmarkSuggestions = result.suggestions.map((s: any) => ({
                ...s,
                suggestionType: 'bookmark'
              }));
              logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Found ${bookmarkSuggestions.length} bookmark suggestions via PostgreSQL text search`);
            }
          } catch (searchError) {
            logger.warn('Bookmark PostgreSQL text search failed:', searchError);
          }
        })()
      );
    }

    // Wait for bookmark search to complete
    if (searchPromises.length > 0) {
      await Promise.all(searchPromises);
    }

    // Combine results
    const allSuggestions = [...componentSuggestions, ...bookmarkSuggestions].slice(0, limit);

    // Count how many of each type are actually in the final result
    const finalComponentCount = allSuggestions.filter(s => s.suggestionType === 'component').length;
    const finalBookmarkCount = allSuggestions.filter(s => s.suggestionType === 'bookmark').length;

    if (allSuggestions.length === 0) {
      sendResponse(id, {
        success: true,
        data: {
          prompt,
          suggestions: [],
          count: 0,
          componentCount: 0,
          bookmarkCount: 0,
          message: 'No matching suggestions found'
        }
      }, sendMessage, wsId);
      return;
    }

    logger.info(`[USER_PROMPT_SUGGESTIONS_REQ ${id}] Returning ${allSuggestions.length} suggestions: ${finalComponentCount} components, ${finalBookmarkCount} bookmarks`);

    sendResponse(id, {
      success: true,
      data: {
        prompt,
        suggestions: allSuggestions,
        count: allSuggestions.length,
        componentCount: finalComponentCount,
        bookmarkCount: finalBookmarkCount,
        message: `Found ${allSuggestions.length} suggestions (${finalComponentCount} components, ${finalBookmarkCount} bookmarks)`
      }
    }, sendMessage, wsId);

  } catch (error) {
    logger.error('Failed to handle user prompt suggestions request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Search components based on prompt keywords
 * Searches in component name, displayName, description, keywords, and category
 * @param prompt - Search prompt
 * @param components - List of components to search in
 * @param limit - Maximum number of results to return
 * @returns Matching components sorted by relevance
 */
function searchComponents(prompt: string, components: Component[], limit: number): Component[] {
  const promptLower = prompt.toLowerCase();
  const promptTokens = promptLower.split(/\s+/).filter(token => token.length > 0);

  // Score each component based on keyword matches
  const scoredComponents = components.map(component => {
    let score = 0;

    const componentName = component.name.toLowerCase();
    const componentDisplayName = (component.displayName || '').toLowerCase();
    const componentDesc = component.description.toLowerCase();
    const componentKeywords = (component.keywords || []).map(k => k.toLowerCase());
    const componentCategory = (component.category || '').toLowerCase();

    // Search in each field with different weights
    for (const token of promptTokens) {
      // Exact name match (highest weight)
      if (componentName === token) {
        score += 10;
      }
      // Name contains token
      else if (componentName.includes(token)) {
        score += 5;
      }

      // DisplayName contains token (high weight - user-friendly name)
      if (componentDisplayName.includes(token)) {
        score += 6;
      }

      // Exact keyword match
      if (componentKeywords.includes(token)) {
        score += 8;
      }
      // Keywords contain token
      else if (componentKeywords.some(k => k.includes(token))) {
        score += 4;
      }

      // Description contains token
      if (componentDesc.includes(token)) {
        score += 2;
      }

      // Category contains token
      if (componentCategory.includes(token)) {
        score += 3;
      }
    }

    return { component, score };
  });

  // Filter out components with score 0, sort by score (descending), and take top results
  return scoredComponents
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ component }) => component);
}

/**
 * Send user prompt suggestions response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'USER_PROMPT_SUGGESTIONS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'runtime',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
